import { appState, setCustomer, useAppDispatch, useAppSelector } from "app";
import {
  toggleQqtUserProfileEndpoint,
  useGetEgerdauCustomerQuery,
  useGetQqtUserProfileQuery,
} from "common/services";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useEffect } from "react";

/**
 * Loads the signed-in user's profile into the `app` slice.
 *
 * The source is chosen by the per-environment `toggleQqtUserProfileEndpoint` toggle:
 * - OFF (default): eGerdau `GET /rest/v2/egerdauNA/users/current`, authenticated with the
 *   eGerdau bearer token.
 * - ON: QQT backend `GET /users/current`, authenticated with the QQT_SESSION cookie. The backend
 *   resolves the same payload from the MuleSoft API using the session's email.
 *
 * Both hooks are always called — rules of hooks — with the inactive one parked on `skipToken`,
 * so exactly one request goes out either way.
 *
 * <p>The two paths carry different credentials, so they have different preconditions. The eGerdau
 * path needs the eGerdau bearer token. The QQT path needs a BFF cookie session, which is what an
 * Okta / Entra ID login produces — there is no eGerdau token in that flow at all, so gating it on
 * `accessToken` would keep the request parked forever.</p>
 *
 * @param accessToken the eGerdau bearer token, when the legacy login produced one.
 * @param sessionAuthenticated whether a QQT BFF cookie session is active (`/auth/me` returned a
 *        principal). Sufficient on its own to authenticate the QQT path.
 */
export const useLoadEgerdauCustomer = (
  accessToken: string | null,
  sessionAuthenticated: boolean = false,
) => {
  const dispatch = useAppDispatch();
  const { customer } = useAppSelector(appState);
  const credentialReady = toggleQqtUserProfileEndpoint
    ? Boolean(accessToken) || sessionAuthenticated
    : Boolean(accessToken);
  const shouldLoad = !customer && credentialReady;

  const { error: egerdauError, data: egerdauData } = useGetEgerdauCustomerQuery(
    shouldLoad && !toggleQqtUserProfileEndpoint ? undefined : skipToken,
  );
  const { error: qqtError, data: qqtData } = useGetQqtUserProfileQuery(
    shouldLoad && toggleQqtUserProfileEndpoint ? undefined : skipToken,
  );

  const data = toggleQqtUserProfileEndpoint ? qqtData : egerdauData;
  const error = toggleQqtUserProfileEndpoint ? qqtError : egerdauError;

  useEffect(() => {
    if (data) {
      dispatch(setCustomer(data));
    }
  }, [data, dispatch]);

  return {
    customer,
    error,
  };
};
